import React, { useEffect, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Breadcrumb,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Grid,
  Tag,
  Popconfirm,
  Avatar,
  Select,
  Tooltip
} from 'antd';
import { Link } from 'react-router-dom';
import {
  SearchOutlined,
  RightOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  GlobalOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { synchronizationService, citiesService } from '../../common/services';
import Can from '../../components/can';

// Couleur avatar dynamique
const getAvatarColor = (text) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'];
  if (!text) return colors[0];
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const SyncCacheYandex = () => {
  // Liste des chauffeurs en cache
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Villes
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Statistiques
  const [stats, setStats] = useState({
    totalCached: 0,
    linkedToTigoon: 0,
    notLinked: 0,
    lastRefresh: null
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Recherche + pagination
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Filtre lié / non lié
  const [linkedFilter, setLinkedFilter] = useState('all'); // 'all', 'linked', 'notLinked'

  // Refresh en cours
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Charger les villes au montage
  useEffect(() => {
    citiesService.getAll()
      .then((res) => {
        const citiesData = res?.data || [];
        const activeCities = citiesData.filter(c => c.isActive);
        setCities(activeCities);
        const defaultCity = activeCities.find(c => c.isDefault);
        if (defaultCity) {
          setSelectedCity(defaultCity._id);
        } else if (activeCities.length > 0) {
          setSelectedCity(activeCities[0]._id);
        }
      })
      .catch((err) => console.error("Erreur chargement villes :", err));
  }, []);

  // Charger les statistiques
  const fetchStats = async () => {
    if (!selectedCity) return;
    try {
      setStatsLoading(true);
      const data = await synchronizationService.getCacheStats(selectedCity);
      if (data && data.stats) {
        // Adapter les noms de champs du backend vers le frontend
        setStats({
          totalCached: data.stats.totalCached || 0,
          linkedToTigoon: data.stats.linkedToTigoon || 0,
          notLinked: data.stats.notLinkedToTigoon || 0,
          lastRefresh: data.stats.lastSyncedAt || null
        });
      }
    } catch (e) {
      console.error('Erreur chargement stats cache :', e);
    } finally {
      setStatsLoading(false);
    }
  };

  // Charger la liste des chauffeurs en cache
  const fetchList = async () => {
    if (!selectedCity) return;
    try {
      setLoading(true);
      const params = { page, limit, cityId: selectedCity };
      if (q && q.trim()) {
        params.q = q.trim();
      }
      if (linkedFilter === 'linked') {
        params.isLinkedToTigoon = true;
      } else if (linkedFilter === 'notLinked') {
        params.isLinkedToTigoon = false;
      }

      const data = await synchronizationService.getCacheDrivers(params);

      if (!data) {
        setItems([]);
        setTotal(0);
        return;
      }

      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Erreur chargement cache :', e);
      message.error('Erreur lors du chargement de la liste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, q, selectedCity, linkedFilter]);

  // Rafraîchir le cache pour une ville
  const handleRefreshCache = async () => {
    if (!selectedCity) {
      message.warning('Veuillez sélectionner une ville');
      return;
    }
    try {
      setRefreshing(true);
      const result = await synchronizationService.refreshCache(selectedCity);
      message.success(`Cache rafraîchi : ${result.added || 0} ajoutés, ${result.updated || 0} mis à jour`);
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur refresh cache :', error);
      const errorMsg = error?.message || 'Une erreur est survenue';
      message.error(errorMsg);
    } finally {
      setRefreshing(false);
    }
  };

  // Rafraîchir le cache pour toutes les villes
  const handleRefreshAllCache = async () => {
    try {
      setRefreshingAll(true);
      const result = await synchronizationService.refreshAllCache();
      message.success(`Cache complet rafraîchi : ${result.totalAdded || 0} ajoutés, ${result.totalUpdated || 0} mis à jour`);
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur refresh all cache :', error);
      const errorMsg = error?.message || 'Une erreur est survenue';
      message.error(errorMsg);
    } finally {
      setRefreshingAll(false);
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Chauffeur',
      key: 'driver',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={isMobile ? 36 : 44}
            style={{
              backgroundColor: getAvatarColor(record.firstName || record.phone),
              fontWeight: 600
            }}
            icon={<UserOutlined />}
          >
            {record.firstName ? record.firstName.charAt(0).toUpperCase() : ''}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: '#262626' }}>
              {record.firstName || ''} {record.lastName || ''}
              {!record.firstName && !record.lastName && <span style={{ color: '#8c8c8c' }}>Non renseigné</span>}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.phone || 'N/A'}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Ville',
      dataIndex: 'city',
      key: 'city',
      render: (city) => (
        <Tag
          icon={<GlobalOutlined />}
          style={{
            backgroundColor: '#e6f7ff',
            color: '#1890ff',
            border: '1px solid #91d5ff',
            borderRadius: 6
          }}
        >
          {city?.name || 'N/A'}
        </Tag>
      ),
      responsive: ['sm'],
    },
    {
      title: 'ID Yandex',
      dataIndex: 'driverProfilId',
      key: 'driverProfilId',
      render: (id) => (
        <Tooltip title={id || 'N/A'} placement="top">
          <Tag style={{ borderRadius: 6, fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}>
            {id ? (id.length > 20 ? id.substring(0, 20) + '...' : id) : 'N/A'}
          </Tag>
        </Tooltip>
      ),
      responsive: ['md'],
    },
    {
      title: 'Statut Yandex',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          working: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'En service' },
          not_working: { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Hors service' },
          fired: { color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', label: 'Licencié' },
          unknown: { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Inconnu' }
        };
        const config = statusConfig[status] || statusConfig.unknown;
        return (
          <Tag
            style={{
              backgroundColor: config.bg,
              color: config.color,
              border: `1px solid ${config.border}`,
              borderRadius: 6,
              padding: '2px 8px'
            }}
          >
            {config.label}
          </Tag>
        );
      },
      responsive: ['sm'],
    },
    {
      title: 'Lié à Tigoon',
      dataIndex: 'isLinkedToTigoon',
      key: 'isLinkedToTigoon',
      render: (isLinked) => (
        isLinked ? (
          <Tag
            icon={<LinkOutlined />}
            style={{
              backgroundColor: '#f6ffed',
              color: '#52c41a',
              border: '1px solid #b7eb8f',
              borderRadius: 6
            }}
          >
            Lié
          </Tag>
        ) : (
          <Tag
            style={{
              backgroundColor: '#fff7e6',
              color: '#fa8c16',
              border: '1px solid #ffd591',
              borderRadius: 6
            }}
          >
            Non lié
          </Tag>
        )
      ),
    },
    {
      title: 'Dernière sync',
      dataIndex: 'lastSyncedAt',
      key: 'lastSyncedAt',
      render: (date) => (
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {formatDate(date)}
        </span>
      ),
      responsive: ['lg'],
    },
  ];

  // Gestion du changement de tableau
  const onTableChange = (pagination) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .sync-cache-table .ant-table-thead > tr > th {
            background: #fafafa !important;
            font-weight: 600;
            color: #595959;
          }
          .sync-cache-table .ant-table-tbody > tr:nth-child(odd) {
            background: #ffffff;
          }
          .sync-cache-table .ant-table-tbody > tr:nth-child(even) {
            background: #fafafa;
          }
          .sync-cache-table .ant-table-tbody > tr:hover > td {
            background: #e6f7ff !important;
          }
          .stat-card-cache {
            border-radius: 12px;
            border: none;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .stat-card-cache:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#1890ff' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#1890ff' }}>Configuration</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Cache Yandex</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Cartes de statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-cache"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Total en cache</span>}
                value={stats.totalCached || 0}
                prefix={<DatabaseOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-cache"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #b7eb8f 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Liés à Tigoon</span>}
                value={stats.linkedToTigoon || 0}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-cache"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Non liés</span>}
                value={stats.notLinked || 0}
                prefix={<UserOutlined style={{ color: '#fa8c16' }} />}
                valueStyle={{ color: '#fa8c16', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-cache"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #f9f0ff 0%, #d3adf7 100%)' }}
            >
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#595959', fontWeight: 500, fontSize: 14 }}>Dernier refresh</span>
              </div>
              <div style={{ color: '#722ed1', fontWeight: 600, fontSize: 14 }}>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                {formatDate(stats.lastRefresh)}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Info Alert */}
        <Alert
          message="Cache des chauffeurs Yandex"
          description={
            <span>
              Ce cache stocke localement la liste des chauffeurs présents dans <strong>Yandex</strong> pour permettre
              une détection rapide lors de l'inscription par un affilié. Cliquez sur <strong>'Rafraîchir le cache'</strong> pour
              synchroniser les données depuis l'API Yandex. Le cache est également rafraîchi automatiquement périodiquement.
            </span>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24, borderRadius: 12 }}
        />

        {/* Carte avec filtre et tableau */}
        <Card
          style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Barre de recherche et actions */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={6} md={4}>
              <Select
                placeholder="Ville"
                value={selectedCity}
                onChange={(val) => { setSelectedCity(val); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                suffixIcon={<GlobalOutlined style={{ color: '#1890ff' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    {city.name} {city.isDefault && '(Par défaut)'}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Select
                placeholder="Statut liaison"
                value={linkedFilter}
                onChange={(val) => { setLinkedFilter(val); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
              >
                <Select.Option value="all">Tous</Select.Option>
                <Select.Option value="linked">Liés à Tigoon</Select.Option>
                <Select.Option value="notLinked">Non liés</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Rechercher par téléphone, nom..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                allowClear
                size={controlSize}
              />
            </Col>
            <Col xs={24} sm={4} md={10} style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                <DatabaseOutlined style={{ marginRight: 6 }} />
                {total} chauffeur{total > 1 ? 's' : ''}
              </div>
              <Button
                icon={<ReloadOutlined />}
                size={controlSize}
                onClick={() => { fetchList(); fetchStats(); }}
                style={{ borderRadius: 8 }}
              >
                Actualiser
              </Button>
              <Can perm="sync:update">
                <Popconfirm
                  title="Rafraîchir le cache ?"
                  description={`Les chauffeurs Yandex de cette ville seront synchronisés.`}
                  onConfirm={handleRefreshCache}
                  okText="Oui, rafraîchir"
                  cancelText="Annuler"
                >
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    size={controlSize}
                    loading={refreshing}
                    style={{
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                      border: 'none'
                    }}
                  >
                    {isMobile ? 'Refresh' : 'Rafraîchir le cache'}
                  </Button>
                </Popconfirm>
              </Can>
              <Can perm="sync:update">
                <Popconfirm
                  title="Rafraîchir le cache de TOUTES les villes ?"
                  description="Cette opération peut prendre du temps."
                  onConfirm={handleRefreshAllCache}
                  okText="Oui, tout rafraîchir"
                  cancelText="Annuler"
                >
                  <Button
                    icon={<SyncOutlined />}
                    size={controlSize}
                    loading={refreshingAll}
                    style={{
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                      border: 'none',
                      color: '#fff'
                    }}
                  >
                    {isMobile ? 'Tout' : 'Tout rafraîchir'}
                  </Button>
                </Popconfirm>
              </Can>
            </Col>
          </Row>

          {/* Tableau */}
          <Table
            className="sync-cache-table"
            rowKey="_id"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={isMobile ? 'small' : 'middle'}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (total, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {total}
                </span>
              ),
            }}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <DatabaseOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
                  <div style={{ color: '#8c8c8c', fontSize: 16, fontWeight: 500 }}>
                    Aucun chauffeur en cache
                  </div>
                  <div style={{ color: '#bfbfbf', marginTop: 8 }}>
                    Cliquez sur "Rafraîchir le cache" pour synchroniser les données Yandex
                  </div>
                </div>
              )
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default SyncCacheYandex;
